import { useStore } from '@nanostores/react'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import { type ComponentProps, useEffect, useMemo } from 'react'
import { useShallow } from 'zustand/shallow'

import type { CountryCode } from '@/services/schemas'
import useCartStore from '@/stores/CartStore'
import useConfigDataStore from '@/stores/ConfigDataStore'
import { $country } from '@/stores/Country'
import useLimitStore from '@/stores/LimitStore'
import { cn } from '@/util/cn'
import { CONTROL_POWER, CONTROL_POWER_DELUXE, PRO_HUBS_IDS } from '@/util/Hardcoded'

const BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3000/parts-images/' : `https://mycontroller.apps.redseafish.com/parts-images/`

const slotVariants = cva('relative flex rounded-[4px] border border-dashed', {
  variants: {
    state: {
      selected: 'border-rsf-green',
      available: 'border-rsf-blue-accent bg-rsf-blue-light',
      disabled: 'border-rsf-grey-border bg-rsf-grey-bg',
    },
    position: {
      horizontal: 'flex-col content-center items-center justify-center px-3 py-2',
      vertical: 'flex-row content-center items-center p-2 md:py-2',
    },
    height: {
      fill: 'h-full',
      fit: 'h-fit',
    },
  },
  defaultVariants: {
    state: 'available',
    position: 'horizontal',
  },
})

const slotBadgeVariants = cva('m-0 rounded-2xl text-center text-[7px] font-bold leading-none text-white md:text-3xs', {
  variants: {
    state: {
      selected: 'bg-rsf-green',
      available: 'bg-rsf-blue',
      disabled: 'bg-rsf-grey',
    },
    position: {
      horizontal: 'absolute -bottom-2 px-2 py-1',
      vertical: 'absolute right-2 z-50 origin-right -rotate-180 text-nowrap px-1 py-2 [writing-mode:vertical-lr]',
    },
  },
  defaultVariants: {
    state: 'available',
    position: 'horizontal',
  },
})

const CountryVariants = ['usa', 'uk', 'aus-chn'] as const
type CountryVariant = typeof CountryVariants[number]
type Variant = 'mobile' | 'pro' | CountryVariant

type SlotPartInfo = {
  image: string
  variants?: Variant[]
  hubId: string
  rNumber?: string
  title?: string
  area?: string
  badgeClassName?: string
}

const CountryVariantMap: Record<CountryVariant, CountryCode[]> = {
  'usa': ['US', 'JA', 'CA'],
  'aus-chn': ['AU', 'CN', 'ZN'], // ? Same switch for Australia and China
  'uk': ['GB', 'IE'],
}

type SlotProps = (VariantProps<typeof slotVariants> & VariantProps<typeof slotBadgeVariants>) & Omit<ComponentProps<'div'>, 'children'> & SlotPartInfo

export default function Slot({
  className,
  state: initialState = 'available',
  title: forceTitle,
  image: imageURL,
  height,
  variants,
  area,
  badgeClassName,
  rNumber,
  hubId,
}: SlotProps) {
  const country = useStore($country)
  const isPro = variants?.includes('pro') && PRO_HUBS_IDS.includes(hubId)
  const countryVariant = country && variants?.find(countryVariant =>
    CountryVariants.includes(countryVariant as CountryVariant)
    && CountryVariantMap[countryVariant as CountryVariant]?.includes(country),
  )
  const setItem = useConfigDataStore(store => store.setItem)

  const cartItem = useCartStore(useShallow(
    store => store.items.find(
      item => item.hubId === hubId && item.rNumber === rNumber,
    ),
  ))

  const hasPower = useCartStore(useShallow(
    store => store.items.some(
      item => item.hubId === hubId && item.rNumber === CONTROL_POWER && item.amount > 0,
    ),
  ))

  const hasPowerDeluxe = useCartStore(useShallow(
    store => store.items.some(
      item => item.hubId === hubId && item.rNumber === CONTROL_POWER_DELUXE && item.amount > 0,
    ),
  ))

  const limitHit = useLimitStore(
    useShallow(state =>
      state.limits.some(limit =>
        limit.categoryId === cartItem?.categoryId
        && limit.hubId === cartItem?.hubId
        && limit.hit),
    ),
  )

  const isNA = hubId === '1' && rNumber === 'R35827'
  const isDisabled = isNA || limitHit

  let state: SlotProps['state'] = useMemo(() =>
    cartItem
      ? (cartItem.amount > 0 || cartItem.preselect)
          ? 'selected'
          : isDisabled
            ? 'disabled'
            : 'available'
      // eslint-disable-next-line react-hooks/exhaustive-deps
      : initialState, [cartItem, initialState, limitHit, hasPowerDeluxe])

  const amount = useMemo(() => {
    return cartItem?.amount && hasPowerDeluxe && hasPower && rNumber === CONTROL_POWER ? cartItem?.amount + 1 : cartItem?.amount ?? 0
  }, [cartItem?.amount, hasPower, hasPowerDeluxe, rNumber])

  if (hasPowerDeluxe && rNumber === CONTROL_POWER) state = 'selected'

  // const [imageWidth, setImageWidth] = useState(0)
  const title = forceTitle || (cartItem?.name ?? '')

  // useEffect(() => {
  //   const image = new Image()
  //   image.src = `${BASE}${imageURL}.png`
  //   image.onload = () => {
  //     setImageWidth(image.width)
  //   }
  //   return () => {
  //     image.onload = null
  //   }
  // }, [imageURL])

  // ? Badge position based on free space using image width
  // uses 8 px per character as reference
  // ! not needed anymore
  // const calculatedPosition = useMemo(
  //   (): SlotProps['position'] => title ? (imageWidth / PX_PER_CHARACTER) > title.length ? 'horizontal' : 'vertical' : 'horizontal',
  //   [imageWidth, title],
  // )
  const position = 'horizontal'

  if (countryVariant) imageURL = imageURL.replace('ROW', countryVariant.toUpperCase())
  if (isPro) imageURL = imageURL.replace('lite', 'pro')

  useEffect(() => {
    setItem(hubId, {
      amount,
      area,
      hubId,
      imageURL: `${BASE}${imageURL}-2x.png`,
      position,
      rNumber,
      state,
      title,
      height,
      label: `${title}${amount && amount > 1 ? ` x${amount}` : ''}`,
    })
  }, [amount, area, height, hubId, imageURL, position, rNumber, setItem, state, title])

  return (
    <div
      className={cn(slotVariants({ position, state, height }), className)}
      style={{ gridArea: area }}
    >
      <picture
        className={cn(state !== 'selected' && 'opacity-30', height === 'fill' && 'w-full')}
      >
        {variants?.includes('mobile') && (
          <source
            media="(max-width: 767px)"
            srcSet={`${BASE}${imageURL}-mob.png 1x, ${BASE}${imageURL}-mob-2x.png 2x`}
            data-html2canvas-ignore="true"
          />
        )}
        <source srcSet={`${BASE}${imageURL}.png 1x, ${BASE}${imageURL}-2x.png 2x`} data-html2canvas-ignore="true" />
        <img
          src={`${BASE}${imageURL}.png`}
          alt={title}
          loading="lazy"
        />
      </picture>
      <div className={cn(slotBadgeVariants({ state, position }), badgeClassName, 'badge')}>
        <p className="max-w-[38ch] text-pretty break-words leading-tight">
          {title}
          {amount && amount > 1 ? ` x${amount}` : ''}
        </p>
      </div>
    </div>
  )
}
